import { useState } from "react";
import {
  AppBar,
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Divider,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import DriveEtaIcon from "@mui/icons-material/DriveEta";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import Image from "../Image";
import CustomButton from "../CustomButton";
import { COLORS } from "../../constants";
import { SouthAfricanRand } from "../../utils/helpers";

// Define styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  borderBottom: `1px solid ${COLORS.border}`,
  color: theme.palette.text.primary,
  zIndex: theme.zIndex.drawer + 1,
}));

// MobileSiteTopbar component
const MobileSiteTopbar = ({
  toggleMobileMenu,
  mobileMenuOpen,
  toggleSearch,
  openSavedVehiclesModal,
  gotoCartPage,
  cartItems,
  totalGarageItems,
  isAuthenticated,
  isLoading,
  gotoLoginPage,
  gotoRegistrationPage,
  performLogout,
  firstName,
  lastName,
  tecfinityAccountName,
  tecfinityAccountNumber,
  tecfinityAvailableCredit,
  toggleCategories,
}) => {
  // Menu sections configuration

  const menuSections = [
    {
      title: "Menu",
      items: [
        { text: "Home", href: "/", onClick: null },
        {
          text: "Shop by category",
          href: "#",
          onClick: toggleCategories,
          icon: <ArrowForwardIcon fontSize="small" />,
        },
        { text: "About", href: "/about", onClick: null },
        { text: "Company", href: "/team", onClick: null },
        { text: "Tradeshow", href: "/tradeshow", onClick: null },
        { text: "Contact Us", href: "/contact-us", onClick: null },
        {
          text: "Merchandising",
          href: "/merchandising",
          onClick: null,
          sx: {
            color: COLORS.themeOrange,
          },
        },
        {
          text: "Trade",
          href: "/trade",
          onClick: null,
          sx: {
            background: COLORS.themeYellow,
            color: COLORS.themeBlack,
            fontWeight: 600,
          },
        },
      ],
    },
    {
      title: "Help Center",
      items: [{ text: "FAQ", href: "/faq", onClick: null }],
    },
    {
      title: "Data & Privacy",
      items: [
        {
          text: "Terms & Conditions",
          href: "/terms-of-service",
          onClick: null,
        },
        { text: "Privacy Policy", href: "/privacy-statement", onClick: null },
        { text: "POPIA Act", href: "/popia", onClick: null },
      ],
    },
  ];

  return (
    <>
      <StyledAppBar position="static">
        <Toolbar
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            position: "relative",
            py: 2,
          }}
        >
          {/* Menu button on the left */}
          <CustomButton
            edge="start"
            onClick={toggleMobileMenu}
            basic
            startIcon={<MenuIcon />}
            color={COLORS.themeBlack}
          />
          {/* Image centered absolutely in the Toolbar */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Image
              src="https://argusweb.azureedge.net/argus-web/logo.svg"
              alt="Logo"
              href="/"
              size="lg"
            />
          </Box>
          {/* Icons on the right */}
          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              sx={{ color: COLORS.themeBlack }}
              onClick={toggleSearch}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={openSavedVehiclesModal}>
              <Badge badgeContent={totalGarageItems} color="error">
                <DriveEtaIcon
                  sx={{ color: COLORS.themeBlack }}
                  fontSize="small"
                />
              </Badge>
            </IconButton>
            <IconButton onClick={gotoCartPage}>
              <Badge badgeContent={cartItems} color="error">
                <ShoppingCartIcon
                  sx={{ color: COLORS.themeBlack }}
                  fontSize="small"
                />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </StyledAppBar>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          width: "75%",
          "& .MuiDrawer-paper": {
            width: "75%",
            boxSizing: "border-box",
            backgroundColor: `${COLORS.background} !important`,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          {/* Header */}
          <Paper
            sx={{
              width: "100%",
              boxShadow: "none",
              borderRadius: 0,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                paddingRight: "5px",
              }}
            >
              <Box sx={{ width: "100%" }} edge="start">
                <Image
                  src="https://argusweb.azureedge.net/argus-web/logo.svg"
                  alt="Logo"
                  size="lg"
                />
              </Box>
              <CustomButton
                sx={{ flexShrink: 0 }}
                onClick={toggleMobileMenu}
                edge="end"
                startIcon={<CloseIcon />}
                color="#333"
                basic
              />
            </Box>
          </Paper>

          {/* Scrollable Menu Content */}
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            <Paper
              sx={{
                width: "100%",
                borderRadius: 0,
                boxShadow: "none",
                mb: 2,
              }}
            >
              {menuSections.map((section, index) => (
                <Box key={index}>
                  <Typography
                    sx={{
                      p: 2,
                      color: COLORS.gray,
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 400,
                      backgroundColor: COLORS.background,
                    }}
                  >
                    {section.title}
                  </Typography>
                  <List
                    sx={{
                      py: `0 !important`,
                    }}
                  >
                    {section.items.map((item, itemIndex) => (
                      <ListItemButton
                        key={itemIndex}
                        sx={{
                          borderBottom: `1px solid ${COLORS.background}`,
                          ...(item.sx || {}),
                        }}
                        href={item.href}
                        onClick={item.onClick}
                      >
                        <ListItemText
                          primary={item.text}
                          sx={{
                            "& .MuiTypography-root": {
                              // Targets the typography inside ListItemText
                              fontSize: "var(--font-size-xxs)",
                              fontWeight: item.sx?.fontWeight,
                              color: item.sx?.color,
                            },
                          }}
                        />
                        {item.icon && (
                          <IconButton sx={{ flexShrink: 0 }} edge="end">
                            {item.icon}
                          </IconButton>
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              ))}
            </Paper>
          </Box>

          {/* Bottom Auth Section - Now in Flex, Not Absolute */}
          {!isLoading && (
            <Paper
              sx={{
                width: "100%",
                borderTop: `1px solid ${COLORS.border}`,
                borderRadius: 0,
                boxShadow: "none",
              }}
            >
              {!isAuthenticated ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    p: 1,
                  }}
                >
                  <CustomButton
                    basic
                    text="Login"
                    sx={{
                      backgroundColor: COLORS.themeRed,
                      color: COLORS.white,
                      textAlign: "center",
                      px: 4,
                      py: 1,
                      fontWeight: 500,
                      borderRadius: 20,
                      "&:hover": {
                        backgroundColor: COLORS.themeRed,
                        opacity: 0.9,
                      },
                    }}
                    onClick={gotoLoginPage}
                  />
                  <CustomButton
                    basic
                    text="Register"
                    sx={{
                      color: COLORS.darkText,
                      textAlign: "center",
                      fontWeight: 500,
                    }}
                    onClick={gotoRegistrationPage}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    p: 1,
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", pl: 1 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        borderRadius: 99,
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: COLORS.themeRed,
                          color: COLORS.white,
                          borderRadius: 99,
                          p: 1,
                          px: 1.5,
                        }}
                      >
                        <PersonIcon />
                      </Box>
                      <Box
                        sx={{ display: "flex", flexDirection: "column", m: 1 }}
                      >
                        {!!tecfinityAccountName && (
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: COLORS.darkText,
                              fontSize: "var(--font-size-xs)",
                              textTransform: "capitalize",
                            }}
                          >
                            {tecfinityAccountName}
                          </Typography>
                        )}
                        {!!tecfinityAccountNumber && (
                          <Typography
                            sx={{
                              fontSize: "var(--font-size-xxs)",
                              color: COLORS.lightText,
                              my: -0.5,
                            }}
                          >
                            Acc No: {tecfinityAccountNumber}
                          </Typography>
                        )}
                        {!!tecfinityAvailableCredit && (
                          <Typography
                            sx={{
                              fontSize: "var(--font-size-xxs)",
                              color: COLORS.lightText,
                              my: -0.5,
                            }}
                          >
                            Credit:{" "}
                            {SouthAfricanRand.format(tecfinityAvailableCredit)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    basic
                    sx={{
                      color: COLORS.darkText,
                      fontWeight: 500,
                      px: 1,
                      py: 1,
                    }}
                    onClick={performLogout}
                  >
                    <LogoutIcon />
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MobileSiteTopbar;
